<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { Link, page, router } from '@inertiajs/svelte';
    import Leaderboard from '../components/Leaderboard.svelte';
    import CaseCard from '../components/CaseCard.svelte';
    import Modal from '../components/Modal.svelte';
    import RichTextEditor from '../components/RichTextEditor.svelte';
    import { show } from '@/routes/cases';
    import UpdateWelcomeMessage from '@/actions/App/Actions/Admin/UpdateWelcomeMessage';

    interface CaseSummary {
        id: number;
        sqid: string;
        date: string;
        history_excerpt: string;
        institution?: string;
        computed_status: string;
        topic_name?: string;
        image_url: string | null;
        image_srcset?: { url: string; width: number }[];
        answer_status?: string | null;
    }

    interface LeaderboardEntry {
        user: { id: number; name: string };
        correct_count: number;
    }

    interface CurrentUserRank {
        rank: number;
        correct_count: number;
    }

    let { cases = [], reveal_at = null, welcome_message = '', leaderboard_enabled = true, leaderboard = [], leaderboard_tied_overflow = 0, leaderboard_current_user = null }: { cases: CaseSummary[]; reveal_at: string | null; welcome_message: string; leaderboard_enabled: boolean; leaderboard: LeaderboardEntry[]; leaderboard_tied_overflow: number; leaderboard_current_user: CurrentUserRank | null } = $props();

    let now = $state(new Date());

    $effect(() => {
        const interval = setInterval(() => { now = new Date(); }, 1000);
        return () => clearInterval(interval);
    });

    const countdown = $derived.by(() => {
        if (!reveal_at) return null;
        const diffMs = new Date(reveal_at).getTime() - now.getTime();
        if (diffMs <= 0) return null;
        const total = Math.floor(diffMs / 1000);
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    });

    const user = $derived($page?.props?.auth?.user as { roles: string[] } | null);
    const isAdmin = $derived(user?.roles?.includes('admin') ?? false);

    let editModalOpen = $state(false);
    let editingMessage = $state('');
    let saving = $state(false);

    function openEditor() {
        editingMessage = welcome_message;
        editModalOpen = true;
    }

    function saveWelcomeMessage() {
        saving = true;
        router.patch(UpdateWelcomeMessage.url(), { welcome_message: editingMessage }, {
            preserveScroll: true,
            onSuccess: () => {
                editModalOpen = false;
            },
            onFinish: () => {
                saving = false;
            },
        });
    }
</script>

<svelte:head>
    <title>Case of the Day</title>
</svelte:head>

<Modal bind:open={editModalOpen} title="Edit Welcome Message" maxWidth="max-w-2xl">
        <div class="space-y-4">
            <RichTextEditor
                value={editingMessage}
                placeholder="Enter a welcome message for the dashboard..."
                extended
                onchange={(html) => { editingMessage = html; }}
            />
            <div class="flex justify-end gap-3">
                <button
                    type="button"
                    onclick={() => { editModalOpen = false; }}
                    class="px-4 py-2 text-sm font-medium text-gray-300 hover:text-gray-100"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onclick={saveWelcomeMessage}
                    disabled={saving}
                    class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    </Modal>

    <div class="grid grid-cols-1 {leaderboard_enabled ? 'lg:grid-cols-3' : ''} gap-8">
        <div class="{leaderboard_enabled ? 'lg:col-span-2' : ''}">
            {#if welcome_message || isAdmin}
                <div class="relative group mb-8">
                    {#if welcome_message}
                        <div class="prose prose-invert prose-lg prose-h1:text-3xl prose-h1:my-2 prose-h2:text-2xl prose-h2:my-2 prose-p:my-1 max-w-none">
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html welcome_message}
                        </div>
                    {:else if isAdmin}
                        <p class="text-gray-400 italic">No welcome message set.</p>
                    {/if}

                    {#if isAdmin}
                        <button
                            type="button"
                            onclick={openEditor}
                            title="Edit welcome message"
                            class="absolute top-0 right-0 p-1.5 rounded-md text-gray-400 opacity-0 group-hover:opacity-100 hover:text-cyan-400 hover:bg-cyan-950 transition-all"
                        >
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor">
                                <path opacity=".4" d="M0 160c0-53 43-96 96-96l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-96 0c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l256 0c17.7 0 32-14.3 32-32l0-96c0-17.7 14.3-32 32-32s32 14.3 32 32l0 96c0 53-43 96-96 96L96 512c-53 0-96-43-96-96L0 160z"/>
                                <path d="M392.4 21.7L368 46.1 465.9 144 490.3 119.6c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0zM334.1 80L172.4 241.7c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L432 177.9 334.1 80z"/>
                            </svg>
                        </button>
                    {/if}
                </div>
            {/if}

            <h1 class="text-2xl font-bold text-gray-100 mb-6">Today's Cases</h1>

            {#if countdown && cases.length > 0}
                <div class="flex items-center gap-2 px-4 py-2.5 mb-6 rounded-lg bg-gray-800 border border-amber-500/30">
                    <svg class="w-4 h-4 text-amber-500 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor">
                        <path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120l0 136c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2 280 120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"/>
                    </svg>
                    <span class="text-sm text-gray-300">Cases close in</span>
                    <span class="text-sm font-bold text-amber-500 tabular-nums">{countdown}</span>
                </div>
            {/if}

            {#if cases.length === 0}
                <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 text-center">
                    <p class="text-gray-500">No cases available today. Check back tomorrow!</p>
                </div>
            {:else}
                <div class="grid grid-cols-1 sm:grid-cols-2 {leaderboard_enabled ? '' : 'lg:grid-cols-3'} gap-6">
                    {#each cases as c (c.id)}
                        {@const [year, month, day] = c.date.split('-')}
                        <Link href={show({ year: +year, month: +month, day: +day, sqid: c.sqid })} class="group bg-gray-900 rounded-lg shadow border border-gray-700 hover:border-gray-600 hover:shadow-md transition-all overflow-hidden flex flex-col">
                            <CaseCard
                                imageUrl={c.image_url}
                                imageSrcset={c.image_srcset}
                                topicName={c.topic_name}
                                historyExcerpt={c.history_excerpt}
                                institution={c.institution}
                                answerStatus={c.answer_status}
                                hoverZoom
                            />
                        </Link>
                    {/each}
                </div>
            {/if}
        </div>

        {#if leaderboard_enabled}
            <div>
                <Leaderboard entries={leaderboard} tiedOverflow={leaderboard_tied_overflow} currentUserRank={leaderboard_current_user} />
            </div>
        {/if}
    </div>
